<script lang="ts" setup>
const props = withDefaults(defineProps<{
    root: SitemapNode;
    title?: any;
    active: (node: any) => boolean;
    toggle: (node: any) => any;
    url?: (node: any) => any;
    children?: (node: SitemapNode) => SitemapNode[];
    expanded?: (node: SitemapNode) => boolean;
}>(), {
    url: () => '#',
    children: (node: SitemapNode) => ([] as SitemapNode[]),
    expanded: (node: SitemapNode) => false,
    title: null
});
</script>

<template>
    <header v-if="title" class="d-flex align-items-center text-body-emphasis">
        {{ title }}
    </header>
    <ul class="menu">
        <li
            v-for="(node, i) in children(props.root)"
            :key="i"
            :class="{ active: active(node) }"
        >
            <h6 v-if="node.header" class="header">
                {{ $t(node.name) }}
            </h6>

            <NuxtLink v-else v-slot="{ href }" :to="url(node)" custom>
                <a :href="href" @click.prevent="toggle(node)">
                    <i :class="node.icon" />
                    <span>
                        {{ $t(node.name) }}

                        <span v-if="node.badge" class="badge bg-primary rounded-pill ms-2">{{ node.badge }}</span>
                    </span>
                    <b v-if="children(node).length > 0"><i class="fas fa-chevron-down" :class="{ 'expanded': expanded(node) }" /></b>
                    <b v-else />
                </a>
            </NuxtLink>

            <ul v-if="children(node).length > 0" :class="{ 'expanded': expanded(node) }">
                <li v-for="(subnode, j) in children(node)" :key="j" :class="{ 'active': active(subnode) }">
                    <h6 v-if="subnode.header" class="header">
                        {{ $t(subnode.name) }}
                    </h6>

                    <NuxtLink v-else v-slot="{ href }" :to="url(subnode)" custom>
                        <a :href="href" @click.prevent="toggle(subnode)">
                            <i class="fas fa-fw" />
                            <span>{{ $t(subnode.name) }}</span>

                            <b v-if="children(subnode).length > 0"><i class="fas fa-chevron-down" :class="{ 'expanded': expanded(subnode) }" /></b>
                            <b v-else />
                        </a>
                    </NuxtLink>

                    <ul v-if="children(subnode).length > 0" :class="{ 'ms-3': true, 'expanded': expanded(subnode) }">
                        <li v-for="(node4, k) in children(subnode)" :key="k" :class="{ 'active': active(node4) }">
                            <NuxtLink v-slot="{ href }" :to="url(node4)" custom>
                                <a :href="href" @click.prevent="toggle(node4)">
                                    <i class="fas fa-fw" />
                                    <span>{{ $t(node4.name) }}</span>
                                </a>
                            </NuxtLink>
                        </li>
                    </ul>
                </li>
            </ul>
        </li>
    </ul>
</template>

<style lang="scss" scoped>
header {
    padding: 0 1rem;
    font-size: 20px;
    font-weight: 600;
    height: var(--ideo-header-height);
}

// pierwszy poziom
ul.menu {
    &, ul {
        padding: 0;
        margin: 0;
        list-style-type: none;
    }

    .fa-chevron-down {
        transition: transform 0.3s;
        transform: rotate(0deg);
    }
    .fa-chevron-down.expanded {
        transition: transform 0.3s;
        transform: rotate(180deg);
    }

    li {
        padding: 0;
        margin: 0;

        > .header {
            margin: 1rem 0 1rem 16px;
            text-transform: uppercase;
            color: var(--bs-tertiary-color);
        }

        > a {
            display: flex;
            flex-direction: row;
            align-items: stretch;
            padding: 7px 0;
            text-decoration: none;
            font-size: 16px;
            font-weight: 500;
            color: var(--ideo-menu-link-color);

            > i {
                margin: 0 var(--ideo-menu-icon-space) 0 calc(var(--ideo-menu-icon-space) - var(--ideo-menu-item-space));
                min-width: 20px;
                line-height: 1.5em;
                display: flex;
                justify-content: center;
                align-items: center;
            }
            > span {
                display: flex;
                flex-grow: 1;
                align-items: center;
            }
            > b {
                display: block;
                margin: 7px 16px 0 0px;
                min-width: 10px;
                text-align: center;
                font-size: 11px;
            }

            &:hover {
                > span, > i, > b {
                    color: var(--ideo-menu-active-color) !important;
                }
            }
        }

        &.active:not(:has(.active)) {
            > a {
                > span, > i, > b {
                    color: var(--ideo-menu-active-color);
                }
            }
        }
    }

    // drugi poziom
    ul {
        display: block;
        overflow: hidden;
        max-height: 0;
        transform: scaleY(0);
        transform-origin: top;
        transition: transform 0.5s cubic-bezier(0, 1, 0.75, 1);

        &.expanded {
            max-height: 1000px;
            transform: scaleY(1);
        }

        > li {
            > .header {
                margin-left: 55px;
            }
            > a {
                font-size: 13px;
                color: var(--ideo-menu-link-color-secondary);
            }
            &:first-child {
                margin-top: var(--ideo-menu-item-space);
            }
            &:last-child {
                margin-bottom: var(--ideo-menu-item-space);
            }
        }
    }
}
</style>
